import {
	Cartesian2,
	Cartesian3,
	defined,
	Entity,
	JulianDate,
	Math as CesiumMath,
	PolygonHierarchy,
	SceneTransforms,
	CzmlDataSource,
	Cartographic,
	Viewer
} from 'cesium';
import { get } from 'svelte/store';
import { useDataSources } from './cesium-datasources';
import { selectedMapEntity } from './cesium-entity-store';
import { cameraState, useCesiumViewer } from './cesium-viewer';
import { isAnyToolEnabled, mapToolsStore } from './map-tools-store';
import {
	cartesianToLatitudeLongitude,
	EntityTypes,
	getCurrentCameraMagnitude,
	getCurrentCesiumCameraPosition,
	getCurrentViewportCenter,
	getEntityPropertyBag,
	getMapEntitiesWithinBoundaries,
	removeAllProtractors
} from './map-utils';
import { mouseCoordinatesStore } from './mouse-coordinates-store';
import { mapModeStore } from './map-mode-store';
import { radiansToDegrees } from '@turf/helpers';
import { handleToolSpecificSelection, handleEntitySelection, redirectLoadCaseLineSelectionIfNeeded } from '$lib/utils/selected-entity-behaviour';
import { toast } from '$lib/components/organisms/toast/toast';

/**
 * An event listener which triggers when Cesium camera has moved. Used to load a specified area of entities.
 *
 * @param cb Callback which does some additional checks based on map-page state.
 *
 */
const registerCameraMoveEndRenderEventListener = async (
	cb: (loadEntities: boolean, centerCoords?: { longitude: number; latitude: number }) => void
) => {
	let cooldown = false;
	const viewer = useCesiumViewer();
	viewer.camera.moveEnd.addEventListener(async () => {
		try {
			if (cooldown) {
				return;
			}
			cooldown = true;
			const position = viewer.camera.position;
			await new Promise((r) => setTimeout(r, 250));
			if (position !== viewer.camera.position) {
				cooldown = false;
				return;
			}
			const CAMERA_ZOOM_THRESHOLD = 5000;

			// If camera is zoomed out, do not load a new area.
			const magnitude = getCurrentCameraMagnitude(viewer);
			if (Math.round(magnitude) > CAMERA_ZOOM_THRESHOLD) {
				cameraState.update((store) => {
					store.loading = false;
					return store;
				});
				cooldown = false;
				return;
			}
			// Update svelte state
			cameraState.update((store) => {
				store.loading = true;
				return store;
			});

			const [centerLng, centerLat] = getEntityAreaToLoad(viewer);

			if (isNaN(centerLat) || isNaN(centerLng)) {
				cooldown = false;
				return;
			}

			cb(true, { longitude: centerLng, latitude: centerLat });
		} catch (e) {
			console.warn(e);
			toast({
				type: 'warning',
				title: 'Kunne ikke laste inn område',
				description: 'Prøv igjen ved å flytte deg i kartet eller trykke på oppdateringsknappen'
			})
		} finally {
			cooldown = false;
		}
	});
};

/**
 * If camera is pitched to a horizontal map view which may point towards a very distant viewport center.
 * Instead use the camera's position to load entities, as it makes for a more reliable .
 * @param viewer
 * @returns [number, number] | undefined
 */
const getEntityAreaToLoad = (viewer: Viewer) => {

	if (Math.abs(viewer.camera.pitch) * 10 < 4) {
		const cameraPosition = getCurrentCesiumCameraPosition(viewer.camera);
		const cameraLatLng = Cartographic.fromCartesian(cameraPosition.position);
		const { longitude, latitude } = cameraLatLng;

		cameraState.update((store) => {
			store.cameraState = 'CAMERA';
			return store;
		});
		return [radiansToDegrees(longitude), radiansToDegrees(latitude)];
	}

	const viewportCenter = getCurrentViewportCenter(viewer);
	cameraState.update((store) => {
		store.cameraState = 'RAY';
		return store;
	});

	if (!viewportCenter) return [];

	return [viewportCenter[0], viewportCenter[1]];
};


const isAllowedToPickFromDatasource = () => {
	const viewer = useCesiumViewer();

	if(!viewer.selectedEntity) return {
		allowedToPick: false,
		activeDataSource: undefined
	};

 	const {
		mapdataDataSource,
		rectangleAreaDataSource,
		distanceAnalysisPolylinesDataSource,
		temporaryConfiguredLinesDataSource,
		measureDistancePolylineDataSource,
		sandbox_LabelDataSource,
		sandbox_RectangleDataSource,
		sandbox_PolylineDataSource,
		sandbox_MarkerDataSource,
		sandbox_BoxDataSource,
		aviationObstacleDataSource,
		trenchEntityDatasource,
		smartdataImageDataSource
	} = useDataSources();
	const entityDatasource = viewer.selectedEntity.entityCollection.owner as CzmlDataSource;
	const activeDataSource = get(mapModeStore).activeDataSource;

	const allowedToPickAtAllTimes = [
		mapdataDataSource,
		activeDataSource,
		rectangleAreaDataSource,
		distanceAnalysisPolylinesDataSource,
		temporaryConfiguredLinesDataSource,
		measureDistancePolylineDataSource,
		sandbox_LabelDataSource,
		sandbox_RectangleDataSource,
		sandbox_PolylineDataSource,
		sandbox_MarkerDataSource,
		sandbox_BoxDataSource,
		aviationObstacleDataSource,
		trenchEntityDatasource,
		smartdataImageDataSource
	];
	
	return {
		allowedToPick: allowedToPickAtAllTimes.some((allowed) => allowed == entityDatasource),
		activeDataSource
	}
}

const initiatePicking = () => {
	const viewer = useCesiumViewer();
	const selectionIndicatorElement =
	viewer.selectionIndicator?.viewModel?.selectionIndicatorElement?.firstChild;

	if (selectionIndicatorElement !== null) {
		(selectionIndicatorElement as HTMLElement).style.fill = 'black';
	}
	removeAllProtractors();

	return selectionIndicatorElement as null | HTMLElement;
}

/**
 * Handles the registration of 'click' (select) events for map entities (poles, lines, etc.)
 * @param viewer - the initialized Cesium viewer instance.
 * @param cb - the callback function which returns the selected map entity.
 */
const registerSelectedEntityChangedEventListener = (
	cb: (value?: null | Entity | undefined) => void
) => {
	const viewer = useCesiumViewer();
	const { temp_referenceDataSource } = useDataSources();
	
	//Double status message as the disallowed entity actually is picked and then set to undefined. THIS CHECK SHOULD BE PLACED WHERE IT INTERCEPTS selectedEntityChanged
	viewer.selectedEntityChanged.addEventListener(async () => {
		const selectionIndicatorElement = initiatePicking();

		if(!defined(viewer.selectedEntity)){
			cb(undefined);
			return
		}

		// Loadcase line entities (rendered by area-mode) are click-throughs to
		// the underlying real line. Redirect before the allowed-to-pick gate so
		// the user doesn't see the misleading "open a plan first" toast.
		if (redirectLoadCaseLineSelectionIfNeeded()) return;

		// If we both have a defined entity and collection
		if (viewer.selectedEntity && viewer.selectedEntity.entityCollection) {
			const toolStore = get(mapToolsStore);
			const {allowedToPick, activeDataSource} = isAllowedToPickFromDatasource(); 

			if (
				!allowedToPick &&
				//@ts-ignore Until we integrate drawing tools in sandbox. We control the datasources in this case, and it always contains a name
				!viewer.selectedEntity?.entityCollection.owner?.name?.startsWith('planDrawing:') &&
				!toolStore.drawMeasurementLine.toolEnabled &&
				!toolStore.drawPolyline.toolEnabled &&
				!toolStore.removeSandboxEntities.toolEnabled
			) {
				//Feedback is handled by each respective tool. If no tools enabled, show general feedback.
				if (
					toolStore.createTerrainProfileFromPoles.toolEnabled ||
					toolStore.drawPolygon.toolEnabled
				) {
					cb(undefined);
				} else if ((viewer.selectedEntity.entityCollection.owner as CzmlDataSource).name === temp_referenceDataSource.name) {
					//Entity is part of a temporary datasource that should not be interacted with
					cb(undefined);
				} else {
					if (viewer.selectedEntity.id.includes('Operationalmarking_label')) {
						cb(undefined);
					}
					if (!viewer.selectedEntity?.id.includes('polygon')) {
						toast({
							type: 'info',
							title: 'Ugyldig valg',
							description: 'Vennligst åpne en plan før du velger mastepunkt eller ledning'
						});
						cb(undefined);
					}
				}
			} else if (activeDataSource && activeDataSource?.show === false) {
				toast({
					type: 'info',
					title: 'Ugyldig valg',
					description: 'Vennligst aktiver planen for å kunne gjøre endringer'
				})
				cb(undefined);
			}
		}

		if(get(isAnyToolEnabled)){
			// When using tools, callback value is always null. Make handle-function return context-specific value if this ever changes.
			handleToolSpecificSelection();
			cb(null);
		} else{
			// Handle entity types and WMS / 3D tile features
			// Both 3D-tiles and WMS layers are rendered as entities in Cesium. 
			// Todo: Refactor to handle 3D-tiles and WMS layers in a separate function.
			const callbackValue = await handleEntitySelection(selectionIndicatorElement);
			cb(callbackValue);
		} 
		
		
	});
};

const registerCesiumMouseoverEventListener = (
	cesiumContainer: HTMLElement
) => {
	const viewer = useCesiumViewer();
	const { sandbox_BoxDataSource } = useDataSources();
	cesiumContainer.addEventListener('mousemove', function (e) {
		const ellipsoid = viewer.scene.globe.ellipsoid;
		// Mouse over the globe to see the cartographic position
		const mousePosition = new Cartesian2(e.clientX, e.clientY);
		const {insertNewBox, addEntityTypes} = get(mapToolsStore);

		if (insertNewBox.toolEnabled && insertNewBox.uuid && !(e.target instanceof HTMLCanvasElement)) {
			sandbox_BoxDataSource.entities.removeById(insertNewBox.uuid);
			mapToolsStore.setNewBoxEntity(undefined, 'ui');
		}

		if (addEntityTypes.toolEnabled && addEntityTypes.uuid && !(e.target instanceof HTMLCanvasElement)) {
			sandbox_BoxDataSource.entities.removeById(addEntityTypes.uuid);
			mapToolsStore.setNewEntityType(undefined);
		}

		if (mousePosition) {
			try{
				const mapPosition = viewer.scene.pickPosition(mousePosition);
				if (mapPosition && mapPosition instanceof Cartesian3) {
					const cartographic = ellipsoid.cartesianToCartographic(mapPosition);
					const mouseLongitudePosition = CesiumMath.toDegrees(cartographic.longitude).toFixed(10);

					const mouseLatitudePosition = CesiumMath.toDegrees(cartographic.latitude).toFixed(10);

					mouseCoordinatesStore.set({
						mouseCoordinates: {
							latitudePosition: mouseLatitudePosition,
							longitudePosition: mouseLongitudePosition
						}
					});

					const windowCoordinates = SceneTransforms.worldToWindowCoordinates(
						viewer.scene,
						mapPosition
					);

					if(!windowCoordinates) return;
				}
			} catch(e) {
				console.error("Mouseover error");
			}
		}
	});
};

export {
	registerCesiumMouseoverEventListener,
	registerSelectedEntityChangedEventListener,
	registerCameraMoveEndRenderEventListener,
	getEntityAreaToLoad
};
